<template>
    <div>
        <v-data-table
            :headers="headers"
            :items="workOrdersFiltered"
            class="elevation-0"
            :mobile-breakpoint="0"
            :loading="loading"
            :height="$vuetify.breakpoint.mdAndDown ? '56vh' : '56vh'"
            :item-class="getRowClass"
            :footer-props="{
                itemsPerPageOptions: [15, 30, 100],
                itemsPerPageText: 'Work Orders Per Page',
            }"
        >
            <template v-slot:top>
                <v-row
                    no-gutter
                    class="mt-0 mx-0 pt-4 pb-0 pb-4"
                    :style="{ 'background-color': '#eeeeee' }"
                >
                    <v-col cols="6" class="d-flex mb-n3">
                        <h1 class="mr-4">WORK ORDERS</h1>
                        <v-btn rounded color="primary" @click="findWO = true">
                            <v-icon class="mr-1">
                                mdi-database-search
                            </v-icon>
                            FIND
                        </v-btn>
                        <ValidationWorkOrders
                            :project-id="projectId || undefined"
                            :user="user"
                            :project-manager="
                                project?.projectManager || undefined
                            "
                        />
                    </v-col>
                    <v-col cols="6" class="d-flex mb-n3">
                        <v-spacer />
                        <v-btn
                            @click="openWoReport"
                            color="primary"
                            class="mx-2"
                            rounded
                            v-if="projects"
                            :disabled="loading"
                            :loading="reportLoading"
                        >
                            REPORT
                        </v-btn>
                        <v-btn
                            rounded
                            color="primary"
                            @click="dailyReportDialog = true"
                            class="mx-2"
                        >
                            {{
                                $vuetify.breakpoint.smAndDown
                                    ? 'Daily Report'
                                    : 'Daily Report'
                            }}
                        </v-btn>
                        <v-btn
                            rounded
                            color="primary"
                            @click="openWorkOrderForm"
                            class="mx-2"
                            v-if="
                                !projectId ||
                                    (!selectedProject?.archive &&
                                        selectedProject?.status !== 'closing')
                            "
                        >
                            <v-icon>
                                mdi-plus
                            </v-icon>
                            {{ $vuetify.breakpoint.smAndDown ? '' : 'NEW' }}
                        </v-btn>
                        <v-btn-toggle
                            v-if="projectId"
                            mandatory
                            borderless
                            :style="{ height: '36px' }"
                        >
                            <v-btn
                                @click="getWOFromSDK()"
                                :style="{
                                    'font-size': '12px',
                                    height: '36px',
                                }"
                                class="px-3 py-1"
                                :disabled="loading"
                            >
                                In Process
                            </v-btn>
                            <v-btn
                                @click="closedWO()"
                                :style="{
                                    'font-size': '12px',
                                    height: '36px',
                                }"
                                class="px-3 py-1"
                                :disabled="loading"
                            >
                                Closed
                            </v-btn>
                        </v-btn-toggle>
                    </v-col>
                </v-row>
            </template>
            <!--HEADERS-->
            <template v-slot:[`header.code`]="{ header }">
                <v-text-field
                    class="py-2 mt-2"
                    :label="header.text"
                    v-model="search"
                    dense
                />
            </template>
            <template v-slot:[`header.project`]="{ header }">
                <v-text-field
                    class="py-2 mt-2"
                    :label="header.text"
                    v-model="projectToFilter"
                    dense
                />
            </template>
            <template v-slot:[`header.notes`]="{ header }">
                <div @click.stop>
                    <v-text-field
                        class="py-2 mt-2"
                        :label="header.text"
                        :value="descriptionToFilter"
                        dense
                        @input="descriptionToFilter = $event"
                    />
                </div>
            </template>
            <template v-slot:[`header.status`]="{ header }">
                <v-text-field
                    class="py-2 mt-2"
                    :label="header.text"
                    v-model="statusToFilter"
                    dense
                />
            </template>
            <template v-slot:[`header.reprocess`]="{ header }">
                <v-select
                    :items="['All', 'Yes', 'No']"
                    :label="header.text"
                    v-model="reprocessToFilter"
                >
                </v-select>
            </template>
            <template v-slot:[`header.warranty`]="{ header }">
                <v-select
                    :items="['All', 'Yes', 'No']"
                    :label="header.text"
                    v-model="warrantyToFilter"
                >
                </v-select>
            </template>
            <!--ITEMS-->
            <template v-slot:[`item.createdBy`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">{{ item.createdBy.name }}</p>
                </div>
            </template>
            <template v-slot:[`item.project`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">
                        {{ item.project?.reference }} - {{ item.project?.name }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.approvedDate`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">
                        {{
                            item.approvedDate
                                ? formatDate(
                                      item.approvedDate.seconds ||
                                          item.approvedDate._seconds,
                                      'L'
                                  )
                                : ''
                        }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.productionStart`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">
                        {{
                            item.productionStart
                                ? formatDate(
                                      item.productionStart.seconds ||
                                          item.productionStart._seconds,
                                      'L'
                                  )
                                : ''
                        }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.notes`]="{ item }">
                <div class="d-flex justify-center">
                    {{ descriptionText(item) }}
                </div>
            </template>
            <template v-slot:[`item.status`]="{ item }">
                <div class="d-flex justify-center">
                    <v-chip
                        :color="statusColor[item.status]"
                        class="white--text text-capitalize"
                    >
                        {{ item.status }}
                    </v-chip>
                </div>
            </template>
            <template v-slot:[`item.reprocess`]="{ item }">
                <div class="d-flex justify-center">
                    <v-icon v-if="item.reprocess" color="primary"
                        >mdi-check</v-icon
                    >
                </div>
            </template>
            <template v-slot:[`item.warranty`]="{ item }">
                <div class="d-flex justify-center">
                    <v-icon v-if="item.warranty" color="primary"
                        >mdi-check</v-icon
                    >
                </div>
            </template>
            <template v-slot:[`item.alerts`]="{ item }">
                <div
                    v-if="item.alertSummary && item.alertSummary.hasAlerts"
                    class="d-flex align-center justify-center"
                >
                    <v-tooltip v-if="item.alertSummary.open > 0" bottom>
                        <template v-slot:activator="{ on }">
                            <v-badge
                                :content="item.alertSummary.open"
                                color="orange"
                                v-on="on"
                            >
                                <v-icon color="orange">mdi-alert-circle</v-icon>
                            </v-badge>
                        </template>
                        <span>{{ item.alertSummary.open }} open alert(s)</span>
                    </v-tooltip>

                    <v-tooltip
                        v-if="item.alertSummary.pendingApproval > 0"
                        bottom
                    >
                        <template v-slot:activator="{ on }">
                            <v-badge
                                :content="item.alertSummary.pendingApproval"
                                color="purple"
                                v-on="on"
                                class="ml-2"
                            >
                                <v-icon color="purple">mdi-clock-alert</v-icon>
                            </v-badge>
                        </template>
                        <span
                            >{{ item.alertSummary.pendingApproval }} pending
                            approval</span
                        >
                    </v-tooltip>

                    <v-tooltip v-if="item.alertSummary.resolved > 0" bottom>
                        <template v-slot:activator="{ on }">
                            <v-badge
                                :content="item.alertSummary.resolved"
                                color="green"
                                v-on="on"
                                class="ml-2"
                            >
                                <v-icon color="green">mdi-check-circle</v-icon>
                            </v-badge>
                        </template>
                        <span
                            >{{ item.alertSummary.resolved }} resolved
                            alert(s)</span
                        >
                    </v-tooltip>
                </div>
                <span v-else class="grey--text">-</span>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
                <div class="d-flex justify-center">
                    <v-btn small icon @click="openProcessWorkOrder(item)">
                        <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn
                        v-if="
                            (item.status == 'inProgress' ||
                                item.status == 'rejected') &&
                                (item.projectManager == user.id ||
                                    item.createdBy.id == user.id ||
                                    (user.permissions &&
                                        user.permissions.includes('deleteWO')))
                        "
                        small
                        icon
                        @click="openDeleteWorkOrder(item)"
                    >
                        <v-icon>mdi-delete</v-icon>
                    </v-btn>
                    <v-btn
                        v-if="
                            enableOptions &&
                                (item.projectManager == user.id ||
                                    item.createdBy.id == user.id) &&
                                item.project?.archive == false &&
                                item.project?.status !== 'closing'
                        "
                        small
                        icon
                        @click="copyWorkOrder(item)"
                    >
                        <v-icon>mdi-content-copy</v-icon>
                    </v-btn>
                    <v-tooltip
                        bottom
                        v-if="showingClosedWO && item.status == 'closed'"
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn
                                small
                                icon
                                @click.stop="openRestoreOrder(item)"
                                v-bind="attrs"
                                v-on="on"
                            >
                                <v-icon color="primary">
                                    mdi-lock-open
                                </v-icon>
                            </v-btn>
                        </template>
                        <span>Restore</span>
                    </v-tooltip>
                </div>
            </template>
        </v-data-table>
        <v-alert
            v-if="activateAlert"
            type="success"
            color="primary"
            :style="{ position: 'absolute', right: '0px', bottom: '0px' }"
        >
            <v-row no-gutters>
                <v-col cols="11">
                    <p class="my-0">{{ alertMessage }}</p>
                </v-col>
                <v-col cols="1">
                    <v-btn
                        small
                        icon
                        class="mt-n1"
                        @click="activateAlert = false"
                    >
                        <v-icon>
                            mdi-close
                        </v-icon>
                    </v-btn>
                </v-col>
            </v-row>
        </v-alert>
        <!--WORK ORDER FORM-->
        <v-dialog
            v-model="workOrder"
            :retain-focus="false"
            persistent
            width="600px"
        >
            <WorkOrderForm
                v-if="workOrder"
                :projectId="projectId"
                :project="localProject"
                :workOrders="workOrders"
                :settings="settings"
                @close="closeWorkOrderForm"
                @addWorkOrder="addWorkOrder"
            />
        </v-dialog>
        <!--PROCESS WORK ORDER-->
        <v-dialog
            v-model="processWorkOrder"
            :retain-focus="false"
            persistent
            fullscreen
        >
            <ProcessWorkOrder
                v-if="processWorkOrder"
                :order="selectedOrder"
                :settings="settings"
                @close="closeProcessWorkOrder"
            />
        </v-dialog>
        <!--PROCESS BOM-->
        <v-dialog
            v-model="processBOM"
            :retain-focus="false"
            persistent
            fullscreen
        >
            <ProcessBOM
                v-if="processBOM"
                :bom="selectedBOM"
                :process="'BOM Detail'"
                @close="closeProcessBOM"
            />
        </v-dialog>
        <!--Delete Work Order-->
        <v-dialog
            v-model="deleteWarning"
            persistent
            max-width="400px"
            :retain-focus="false"
        >
            <v-card :loading="loading" v-if="deleteWarning">
                <v-card-title class="text-h5">Delete Work Order</v-card-title>
                <v-card-text>
                    Are you sure you want to delete this work order?
                </v-card-text>
                <v-card-actions>
                    <v-btn text color="secondary" @click="closeDeleteWorkOrder">
                        Close
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        @click="deleteWorkOrder"
                        text
                        color="error"
                        :loading="loading"
                    >
                        Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <!--FIND WO-->
        <v-dialog
            v-model="findWO"
            :retain-focus="false"
            persistent
            max-width="600"
        >
            <FindWO v-if="findWO" @addWO="addWO" @close="findWO = false" />
        </v-dialog>
        <v-dialog max-width="450px" persistent v-model="dailyReportDialog"
            ><DailyWOReport
                v-if="dailyReportDialog"
                @closeDailyReport="dailyReportDialog = false"
            ></DailyWOReport
        ></v-dialog>
        <!-- Report dialog -->
        <v-dialog max-width="450px" v-model="woReportDialog" persistent>
            <WorkOrdersReport
                v-if="woReportDialog"
                :projects="projects"
                :clientNames="clientNames"
                :userNames="userNames"
                :processes="reportProcesses"
                :processesNames="reportProcessesNames"
                :processesData="reportProcessesData"
                :finishAndUCCodes="reportFinishAndUCCodes"
                :productTypes="reportProductTypes"
                :products="reportProducts"
                @closeWoReport="closeWoReport"
            ></WorkOrdersReport>
        </v-dialog>
        <!-- Restore work order -->
        <v-dialog v-model="restoreOrderDialog" persistent max-width="400px">
            <v-card :loading="loading">
                <v-card-title class="text-h5">Restore Work Order</v-card-title>
                <v-card-text>
                    Are you sure you want to restore this work order?
                </v-card-text>
                <v-card-actions>
                    <v-btn text color="secondary" @click="closeRestoreOrder">
                        Cancel
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        @click="restoreOrder"
                        text
                        color="primary"
                        :loading="loading"
                    >
                        OK
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import moment from 'moment'
import _ from 'lodash'
import { firestore } from '@/services/firebase'
import API from '@/services/api'
import { mapMutations, mapGetters, mapState, mapActions } from 'vuex'
import WOMethods from './WOMethods.js'

export default {
    name: 'MainWorkOrders',
    props: {
        projectId: {
            type: String,
            default: () => undefined,
        },
        project: {
            type: Object,
            deafult: () => ({}),
        },
    },
    components: {
        WorkOrderForm: () =>
            import('@/components/WorkOrders/WorkOrderForm.vue'),
        ProcessWorkOrder: () =>
            import('@/components/WorkOrders/ProcessWorkOrder.vue'),
        ProcessBOM: () => import('@/components/WorkOrders/ProcessBOM.vue'),
        FindWO: () => import('@/components/WorkOrders/FindWO.vue'),
        DailyWOReport: () =>
            import('@/components/WorkOrders/DailyWOReport.vue'),
        WorkOrdersReport: () =>
            import('@/components/WorkOrders/WorkOrdersReport.vue'),
        ValidationWorkOrders: () =>
            import('@/components/WorkOrders/ValidationWorkOrders.vue'),
    },
    data: () => ({
        deleteWarning: false,
        selectedOrder: undefined,
        processWorkOrder: false,
        workOrder: false,
        processBOM: false,
        selectedBOM: undefined,
        statusToFilter: undefined,
        statusToFilterDebounced: undefined,
        projectToFilter: undefined,
        projectToFilterDebounced: undefined,
        typeToFilter: undefined,
        typeToFilterDebounced: undefined,
        descriptionToFilter: '',
        loading: false,
        workOrders: [],
        activateAlert: false,
        alertMessage: undefined,
        search: null,
        alertsListener: null,
        searchDebounced: null,
        headers: [
            {
                text: 'ORDER',
                value: 'code',
                align: 'center',
                sortable: false,
            },
            {
                text: 'CREATED BY',
                value: 'createdBy',
                align: 'center',
                sortable: false,
            },
            {
                text: 'PROJECT',
                value: 'project',
                align: 'center',
                sortable: false,
            },
            {
                text: 'APPROVED DATE',
                value: 'approvedDate',
                align: 'center',
                sortable: true,
            },
            {
                text: 'PRODUCTION START',
                value: 'productionStart',
                align: 'center',
                sortable: true,
            },
            {
                text: 'DESCRIPTION',
                value: 'notes',
                align: 'center',
                sortable: false,
            },
            {
                text: 'STATUS',
                value: 'status',
                align: 'center',
                sortable: false,
            },
            {
                text: 'REPROCESS',
                value: 'reprocess',
                align: 'center',
                sortable: false,
                width: 120,
            },
            {
                text: 'WARRANTY',
                value: 'warranty',
                align: 'center',
                sortable: false,
                width: 100,
            },
            {
                text: 'ALERTS',
                value: 'alerts',
                align: 'center',
                sortable: false,
                width: 100,
            },
            {
                text: 'ACTIONS',
                value: 'actions',
                align: 'center',
                sortable: false,
            },
        ],
        company: JSON.parse(localStorage.getItem('company')),
        statusColor: {
            sent: '#FF9800',
            approved: '#27BC4F',
            production: '#FFC000',
            rejected: 'red',
            closed: 'primary',
            inProgress: '#9E9FA0',
            finished: '#5c5c5c',
        },
        listener: undefined,
        companyId: JSON.parse(localStorage.getItem('company')),
        enableOptions: true,
        user: {},
        resourceId: undefined,
        settingProcesses: undefined,
        users: {},
        projects: {},
        dates: [],
        findWO: false,
        qualityProcess: {
            id: 'quality',
            name: 'Quality',
        },
        readyForPackingProcess: {
            id: 'readyForPacking',
            name: 'Ready for packing',
        },
        packingProcess: {
            id: 'packing',
            name: 'Packing',
        },
        warrantyToFilter: 'All',
        reprocessToFilter: 'All',
        woReportDialog: false,
        reportLoading: false,
        reportLookupsReady: false,
        settings: [],
        dailyReportDialog: false,
        clientNames: {},
        userNames: {},
        reportProcesses: [],
        reportProcessesNames: {},
        reportProcessesData: {},
        reportFinishAndUCCodes: {},
        reportProductTypes: {},
        reportProducts: {},
        localProject: {},
        restoreOrderDialog: false,
        showingClosedWO: false,
        woMethods: new WOMethods(),
        deletedWorkOrdersIds: [],
        canApproveAlerts: false,
        extraProjectFetches: 0,
        coordinatorProcesses: [],
        pendingWoFieldSaves: {
            description: false,
            notes: false,
            localValues: {},
        },
    }),
    provide() {
        return {
            approveWO: this.approveWO,
            pendingWoFieldSaves: this.pendingWoFieldSaves,
        }
    },
    computed: {
        ...mapState(['notificationResource']),
        selectedProject() {
            if (this.localProject && this.localProject.id) {
                return this.localProject
            }
            if (this.projects && this.projectId) {
                return this.projects.find(
                    project => project.id == this.projectId
                )
            }
            return undefined
        },
        workOrdersFiltered() {
            let conditions = []
            if (this.searchDebounced) {
                conditions.push(this.filterOrder)
            }
            if (this.typeToFilterDebounced) {
                conditions.push(this.filterByType)
            }
            if (this.projectToFilterDebounced) {
                conditions.push(this.filterProject)
            }
            const descriptionFilter = (this.descriptionToFilter || '')
                .toString()
                .trim()
                .toLowerCase()
            if (descriptionFilter) {
                conditions.push(order =>
                    this.orderMatchesDescription(order, descriptionFilter)
                )
            }
            if (this.statusToFilterDebounced) {
                conditions.push(this.filterStatus)
            }
            conditions.push(this.filterByReprocess)
            conditions.push(this.filterByWarranty)
            if (conditions.length > 0) {
                return this.workOrders.filter(order => {
                    return conditions.every(condition => {
                        return condition(order)
                    })
                })
            }
            return this.workOrders
        },
    },
    watch: {
        notificationResource: function(resource) {
            if (resource) {
                this.resourceId = resource
                this.openResource()
                this.setNotificationResource(undefined)
            }
        },
        '$route.query.bomId': function(newBomId, oldBomId) {
            if (newBomId && this.workOrders.length > 0) {
                this.handleBomIdParameter()
            } else if (!newBomId && oldBomId) {
                if (this.processBOM) {
                    this.closeProcessBOM()
                }
            }
        },
        '$route.query.orderId': function(newOrderId) {
            if (newOrderId) {
                this.handleOrderCodeParameter()
            }
        },
        search: function(newValue) {
            this.debouncedSearch(newValue)
        },
        projectToFilter: function(newValue) {
            this.debouncedProjectFilter(newValue)
        },
        typeToFilter: function(newValue) {
            this.debouncedTypeFilter(newValue)
        },
        statusToFilter: function(newValue) {
            this.debouncedStatusFilter(newValue)
        },
    },
    created() {
        this.debouncedSearch = _.debounce(value => {
            this.searchDebounced = value
        }, 400)
        this.debouncedProjectFilter = _.debounce(value => {
            this.projectToFilterDebounced = value
        }, 400)
        this.debouncedTypeFilter = _.debounce(value => {
            this.typeToFilterDebounced = value
        }, 400)
        this.debouncedStatusFilter = _.debounce(value => {
            this.statusToFilterDebounced = value
        }, 400)
    },
    async mounted() {
        try {
            this.loading = true
            // open bom
            if (this.$route.query.bomId) {
                this.handleBomIdParameter()
            }

            // handle orderId parameter
            if (this.$route.query.orderId) {
                this.handleOrderCodeParameter()
            }
            // Get project
            this.localProject = this.project
            this.resourceId = this.$route.query.resourceId

            const projectPromise =
                this.projectId && !this.localProject
                    ? API.getProject(this.projectId)
                    : Promise.resolve(null)

            this._settingsPromise = API.getSettings()
            const [
                {
                    data: { user },
                },
                projects,
                {
                    data: { users },
                },
                fetchedProject,
            ] = await Promise.all([
                API.getMyInfo(),
                API.getWoProjects(),
                API.getWoUsers(),
                projectPromise,
            ])
            if (fetchedProject) {
                this.localProject = fetchedProject
            }

            this.user = user
            this.projects = projects
            this.users = users
            this.canApproveAlerts = this.user.permissions.includes(
                'approveProductionAlerts'
            )

            await this.getWOFromSDK()

            try {
                const {
                    data: { settings },
                } = await this._settingsPromise
                this.settings = settings
                const processesSetting = this.settings.find(
                    s => s.name == 'Processes'
                )
                if (processesSetting) {
                    const processes = [...(processesSetting.processes || [])]
                    processes.push(this.qualityProcess, this.packingProcess)
                    this.settingProcesses = {
                        ...processesSetting,
                        processes,
                    }
                }
                this.coordinatorProcesses = (processesSetting?.processes || [])
                    .filter(p => p?.coordinators?.includes(this.user.id))
                    .map(p => p.id)
            } catch (error) {
                this.coordinatorProcesses = []
            }
            this.setupRealtimeAlertsListener()
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    beforeDestroy() {
        if (this.listener) {
            this.listener()
            this.listener = null
        }
        if (this.alertsListener) {
            this.alertsListener()
            this.alertsListener = null
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        ...mapActions(['setNotificationResource']),
        ...mapGetters(['getNotificationResource']),
        closeDeleteWorkOrder() {
            this.selectedOrder = {}
            this.deleteWarning = false
        },
        openDeleteWorkOrder(item) {
            this.selectedOrder = _.cloneDeep(item)
            this.deleteWarning = true
        },
        async copyWorkOrder(item) {
            try {
                this.loading = true
                let workOrderToCopy = item

                const hasPartNumbers =
                    workOrderToCopy.partNumbers &&
                    Object.keys(workOrderToCopy.partNumbers).length > 0

                if (!hasPartNumbers && item.status !== 'deleted') {
                    const docRef = firestore
                        .collection('companies')
                        .doc(this.companyId)
                        .collection('workOrders')
                        .doc(item.id)
                    const doc = await docRef.get()
                    if (doc.exists) {
                        workOrderToCopy = { id: doc.id, ...doc.data() }
                    }
                }

                await API.copyWorkOrder(workOrderToCopy)
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async deleteWorkOrder() {
            try {
                this.loading = true
                await API.deleteWorkOrder(this.selectedOrder.id)
                this.closeDeleteWorkOrder()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        addWorkOrder() {
            this.activateAlert = true
            this.alertMessage = 'New Work Order Successfully Created'
        },
        async openProcessWorkOrder(item) {
            try {
                this.loading = true
                let order = await this.woMethods.hydrateWorkOrderOnOpen(
                    this.companyId,
                    item
                )
                order = _.cloneDeep(order)
                order = (await this.formatWorkOrder(order)) || order
                if (!order.project || Object.keys(order.project).length == 0) {
                    order.project =
                        this.projects[order.projectId] ||
                        this.localProject ||
                        item.project ||
                        (await API.getProject(order.projectId))
                    if (order.projectId && order.project) {
                        this.projects[order.projectId] = order.project
                    }
                }
                this.selectedOrder = _.cloneDeep(order)
                this.processWorkOrder = true
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        closeProcessWorkOrder() {
            this.processWorkOrder = false
        },
        closeProcessBOM() {
            this.processBOM = false
            this.selectedBOM = undefined
            this.clearBomIdFromUrl()
        },
        openWorkOrderForm() {
            this.workOrder = true
        },
        closeWorkOrderForm() {
            this.workOrder = false
        },
        filterOrder(order) {
            return order.code
                .toLowerCase()
                .includes(this.searchDebounced.toLowerCase())
        },
        filterByType(order) {
            const type = order.assemblyWorkOrder ? 'assembly' : 'production'
            return type.includes(this.typeToFilterDebounced.toLowerCase())
        },
        filterStatus(order) {
            return order.status
                .toLowerCase()
                .includes(this.statusToFilterDebounced.toLowerCase())
        },
        filterProject(order) {
            return (
                order.project?.name
                    .toLowerCase()
                    .includes(this.projectToFilterDebounced.toLowerCase()) ||
                order.project?.reference
                    .toString()
                    .toLowerCase()
                    .includes(this.projectToFilterDebounced.toLowerCase())
            )
        },
        descriptionText(order) {
            if (!order) {
                return ''
            }
            const notes = order.notes == null ? '' : String(order.notes).trim()
            if (notes) {
                return notes
            }
            return order.description == null ? '' : String(order.description)
        },
        orderMatchesDescription(order, term) {
            if (!order || !term) {
                return false
            }
            const notes = order.notes == null ? '' : String(order.notes)
            const description =
                order.description == null ? '' : String(order.description)
            return `${notes} ${description}`.toLowerCase().includes(term)
        },
        filterByReprocess(order) {
            if (this.reprocessToFilter == 'Yes') {
                return order.reprocess
            } else if (this.reprocessToFilter == 'No') {
                return !order.reprocess
            } else {
                return true
            }
        },
        filterByWarranty(order) {
            if (this.warrantyToFilter == 'Yes') {
                return order.warranty
            } else if (this.warrantyToFilter == 'No') {
                return !order.warranty
            } else {
                return true
            }
        },
        formatDate(seconds, format) {
            if (format == 'L') {
                return `${moment.unix(seconds).format('L')} ${moment
                    .unix(seconds)
                    .format('LT')}`
            } else {
                return `${moment.unix(seconds).format('YYYY-MM-DD')}`
            }
        },
        approveWO() {
            this.activateAlert = true
            this.alertMessage = 'WO Successfully Approved'
        },
        sort(list = this.workOrders) {
            if (!Array.isArray(list)) {
                return
            }
            list.sort(
                (a, b) =>
                    (b.createdOn.seconds || b.createdOn._seconds) -
                    (a.createdOn.seconds || a.createdOn._seconds)
            )
        },
        openResource() {
            if (this.resourceId) {
                const resource = this.workOrders.find(
                    w => w.id == this.resourceId
                )
                if (resource) {
                    this.resourceId = undefined
                    this.openProcessWorkOrder(resource)
                }
            }
        },

        async loadAllActiveWorkOrders() {
            return await this.woMethods.loadAllActiveWorkOrders(
                this.companyId,
                null,
                true
            )
        },
        async getWOFromSDK() {
            try {
                this.loading = true
                this.extraProjectFetches = 0
                // Reset showingClosedWO when switching back to In Process
                this.showingClosedWO = false

                const projectFilterRoutes = [
                    'project-work-orders',
                    'archive-project-work-orders',
                    'project-work-orders-generic',
                ]

                const filterByProject = projectFilterRoutes.includes(
                    this.$router.currentRoute.name
                )
                const projectIdFilter = this.projectId

                let startDate = new Date()

                // if project view, disable options
                let query = firestore
                    .collection('companies')
                    .doc(this.companyId)
                    .collection('workOrders')
                    .where('updatedOn', '>=', startDate)

                let initialData = []
                if (filterByProject) {
                    initialData = await API.getLiteWorkOrdersByProject({
                        projectId: projectIdFilter,
                        includeClosed: false,
                    })
                    this.enableOptions = false
                } else {
                    // Cargar todos los work orders en lotes usando paginación
                    initialData = await this.loadAllActiveWorkOrders()
                    initialData = initialData.filter(
                        wo => wo.status !== 'finished'
                    )
                }

                let data = []
                for (let wo of initialData) {
                    wo = await this.formatWorkOrder(wo)
                    if (wo) {
                        data.push(wo)
                    }
                }

                this.workOrders = data
                this.sort()
                this.openResource()
                this.loading = false
                this.listener = query.onSnapshot(async snapshot => {
                    // Don't update if user is viewing closed work orders
                    if (this.showingClosedWO) {
                        return
                    }

                    const changes = Array.from(snapshot.docChanges())
                    const processedChanges = await Promise.all(
                        changes.map(async change => {
                            let wo = Object.assign(change.doc.data(), {
                                id: change.doc.id,
                            })
                            wo = await this.formatWorkOrder(wo)
                            return { change, wo }
                        })
                    )

                    // Procesar todos los cambios después de que todos se hayan formateado
                    processedChanges.forEach(({ change, wo }) => {
                        if (wo) {
                            if (
                                change.type === 'added' ||
                                change.type === 'modified'
                            ) {
                                const index = data.findIndex(w => w.id == wo.id)
                                if (index >= 0) {
                                    // Usar $set para asegurar reactividad de Vue
                                    this.$set(data, index, wo)
                                } else {
                                    if (filterByProject && projectIdFilter) {
                                        if (wo.projectId === projectIdFilter) {
                                            data.push(wo)
                                        }
                                    } else {
                                        data.push(wo)
                                    }
                                }
                            }
                            if (
                                change.type === 'removed' ||
                                (!filterByProject &&
                                    wo.status === 'finished') ||
                                wo.status == 'deleted' ||
                                wo.status === 'closed'
                            ) {
                                const index = data.findIndex(w => w.id == wo.id)
                                if (index >= 0) {
                                    data.splice(index, 1)
                                }
                            }
                        }
                    })
                    this.sort(data)
                    // Forzar actualización del array para asegurar reactividad
                    this.workOrders = [...data]
                    this.openResource()
                })
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async formatWorkOrder(wo) {
            try {
                // deduplicate and prepare work orders
                const workOrder = wo
                //verify user
                if (
                    this.$router.currentRoute.name != 'project-work-orders' &&
                    this.$router.currentRoute.name !=
                        'archive-project-work-orders' &&
                    this.$router.currentRoute.name !=
                        'project-work-orders-generic'
                ) {
                    const createdById = this.woMethods.createdById(
                        workOrder.createdBy
                    )
                    if (
                        !this.user?.permissions?.includes('readAllWO') &&
                        createdById != this.user.id &&
                        workOrder.projectManager != this.user.id
                    ) {
                        return undefined
                    }
                }

                //verify wo status
                // exclude finished work orders
                // if (
                //     this.$router.currentRoute.name != 'project-work-orders' &&
                //     this.$router.currentRoute.name !=
                //         'archive-project-work-orders' &&
                //     this.$router.currentRoute.name !=
                //         'project-work-orders-generic' &&
                //     workOrder.status == 'finished'
                // ) {
                //     return undefined
                // }

                this.woMethods.assignCreatedBy(workOrder, this.users)

                // get or fetch project
                workOrder.project = this.projects[workOrder.projectId] || {}
                if (
                    !workOrder.project ||
                    Object.keys(workOrder.project).length == 0
                ) {
                    this.extraProjectFetches += 1
                    workOrder.project = await API.getProject(
                        workOrder.projectId
                    )
                    // add project to projects object
                    this.projects[workOrder.projectId] = workOrder.project
                }

                // update selectedOrder if it's currently open
                if (
                    this.selectedOrder &&
                    this.selectedOrder.id &&
                    this.selectedOrder.id == workOrder.id
                ) {
                    const updatedOrder = this.woMethods.formatItems(workOrder)
                    if (updatedOrder) {
                        const orderCopy = _.cloneDeep(updatedOrder)
                        const pending = this.pendingWoFieldSaves
                        // Preserve local text fields while a save is still in flight
                        // so a status snapshot does not wipe unsaved/unconfirmed edits.
                        if (
                            pending.notes &&
                            pending.localValues.notes !== undefined
                        ) {
                            orderCopy.notes = pending.localValues.notes
                        }
                        if (
                            pending.description &&
                            pending.localValues.description !== undefined
                        ) {
                            orderCopy.description =
                                pending.localValues.description
                        }
                        this.selectedOrder = orderCopy
                    }
                }
                return workOrder
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        async closedWO() {
            try {
                this.showingClosedWO = true
                // Uunsubscribe from the listener when switching to closed work orders
                if (this.listener) {
                    this.listener()
                    this.listener = null
                }
                this.workOrders = []
                this.loading = true
                this.dates[0] = this.formatDate(
                    this.localProject.createdOn._seconds
                )
                let newDate = new Date()
                this.dates[1] = `${newDate.getFullYear()}-${newDate.getMonth() +
                    1}-${newDate.getDate()}`
                this.workOrders = await API.getClosedWorkOrders({
                    companyId: this.companyId,
                    startDate: this.dates[0],
                    endDate: this.dates[1],
                    projectId: this.projectId,
                })
                for (let WO of this.workOrders) {
                    WO = this.woMethods.formatItems(WO)
                    WO.project = this.projects[WO.projectId] || {}
                    if (!WO.project || Object.keys(WO.project).length == 0) {
                        WO.project = await API.getProject(WO.projectId)
                        // add project to projects object
                        this.projects[WO.projectId] = WO.project
                    }
                }
                for (const WO of this.workOrders) {
                    this.woMethods.assignCreatedBy(WO, this.users)
                }
                this.sort()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async addWO(wo) {
            try {
                this.loading = true
                const index = this.workOrders.findIndex(w => w.id == wo.id)
                if (index >= 0) {
                    this.openProcessWorkOrder(this.workOrders[index])
                } else {
                    wo.project = await API.getProject(wo.projectId)
                    wo.projectManager = this.users[wo.project.projectManager]
                    wo.creator = this.users[
                        this.woMethods.createdById(wo.createdBy)
                    ]
                    wo.approvedBy = this.users[wo.approvedBy]
                    this.openProcessWorkOrder(wo)
                }
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        closeWoReport() {
            this.woReportDialog = false
        },
        async prefetchMissingProjects(workOrders) {
            const missing = []
            const seen = new Set()
            for (const workOrder of workOrders) {
                const projectId = workOrder && workOrder.projectId
                if (!projectId || seen.has(projectId)) {
                    continue
                }
                const project = this.projects[projectId]
                if (!project || Object.keys(project).length === 0) {
                    seen.add(projectId)
                    missing.push(projectId)
                }
            }
            if (missing.length === 0) {
                return
            }
            await this.woMethods.mapInBatches(
                missing,
                async projectId => {
                    try {
                        const project = await API.getProject(projectId)
                        this.projects[projectId] = project
                        return project
                    } catch (error) {
                        return null
                    }
                },
                10
            )
        },
        async ensureReportLookups() {
            if (this.reportLookupsReady) {
                return
            }
            if (!this.settings.length && this._settingsPromise) {
                const {
                    data: { settings },
                } = await this._settingsPromise
                this.settings = settings
            }
            if (!Object.keys(this.clientNames).length) {
                const {
                    data: { clients },
                } = await API.getLiteClients()
                clients.forEach(client => {
                    this.clientNames[client.id] = client.name
                })
            }
            Object.entries(this.users).forEach(([id, user]) => {
                this.userNames[id] = user.name
            })
            Object.values(this.projects).forEach(project => {
                if (project?.client && !project.clientName) {
                    project.clientName = this.clientNames[project.client]
                }
            })
            const processesSetting = this.settings.find(
                s => s.name.toLowerCase() === 'processes'
            )
            this.reportProcesses = processesSetting?.processes || []
            this.reportProcesses.forEach(process => {
                this.reportProcessesNames[process.id] = process.name
                this.reportProcessesData[process.id] = process
            })
            const finishes =
                this.settings.find(
                    s => s.name.toLowerCase() === 'finishanduccode'
                )?.finishes || []
            finishes.forEach(finish => {
                this.reportFinishAndUCCodes[finish.id] = finish.description
            })
            const categories =
                this.settings.find(s => s.name.toLowerCase() === 'invoicing')
                    ?.categories || []
            categories.forEach(category => {
                this.reportProductTypes[category.id] = category.name
            })
            const products =
                this.settings.find(s => s.name.toLowerCase() === 'products')
                    ?.products || []
            products.forEach(product => {
                this.reportProducts[product.abbreviation] = product.name
            })
            this.reportLookupsReady = true
        },
        async openWoReport() {
            try {
                this.reportLoading = true
                await this.ensureReportLookups()
                this.woReportDialog = true
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.reportLoading = false
            }
        },
        clearBomIdFromUrl() {
            if (this.$route.query.bomId) {
                this.$router.replace({
                    query: { ...this.$route.query, bomId: undefined },
                })
            }
        },
        async handleBomIdParameter() {
            if (this.$route.query.bomId) {
                try {
                    this.loading = true

                    const bom = await API.getBOMById({
                        bomId: this.$route.query.bomId,
                    })

                    if (bom) {
                        this.selectedBOM = bom
                        this.processBOM = true
                    }
                } catch (error) {
                    this.setErrorItems({
                        source: this.$options.name,
                        message: error.message,
                    })
                } finally {
                    this.loading = false
                }
            }
        },
        async handleOrderCodeParameter() {
            if (this.$route.query.orderId) {
                try {
                    this.loading = true
                    if (this.$route.query.code) {
                        await this.searchWO(this.$route.query.code)
                    }
                } catch (error) {
                    this.setErrorItems({
                        source: this.$options.name,
                        message: error.message,
                    })
                } finally {
                    this.loading = false
                }
            }
        },
        async searchWO(code) {
            try {
                this.loading = true
                const response = await API.findWOByCode(code)
                if (response) {
                    const formattedWO = this.woMethods.formatItems(response)
                    await this.addWO(formattedWO)
                }
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        openRestoreOrder(order) {
            this.selectedOrder = _.cloneDeep(order)
            this.restoreOrderDialog = true
        },
        closeRestoreOrder() {
            this.selectedOrder = {}
            this.restoreOrderDialog = false
        },
        async restoreOrder() {
            try {
                this.loading = true
                await API.restoreWorkOrder(
                    this.selectedOrder.id,
                    this.selectedOrder.code
                )
                this.workOrders = this.workOrders.filter(
                    order => order.id != this.selectedOrder.id
                )
                this.closeRestoreOrder()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async loadAlertSummaries() {
            // Load alert summaries for all work orders in a single batch call
            if (this.workOrders.length === 0) return
            try {
                const workOrderIds = this.workOrders.map(wo => wo.id)
                const summaries = await API.getAlertsSummaries(workOrderIds)

                // Attach summaries to work orders
                this.workOrders.forEach(wo => {
                    const summary = summaries[wo.id] || {
                        total: 0,
                        open: 0,
                        resolved: 0,
                        hasAlerts: false,
                        allResolved: false,
                    }
                    this.$set(wo, 'alertSummary', summary)
                })
            } catch (error) {
                // If error, set empty summaries
                this.workOrders.forEach(wo => {
                    const emptySummary = {
                        total: 0,
                        open: 0,
                        resolved: 0,
                        hasAlerts: false,
                        allResolved: false,
                    }
                    this.$set(wo, 'alertSummary', emptySummary)
                })
            }
        },
        /**
         * Same visibility idea as AlertsDashboard / coordinator PM flows.
         * Used so unresolved users only get open + resolved counts for alerts they can see.
         */
        _alertIsVisibleToCurrentUser(alert) {
            const currentUserId = this.user.id
            const processId = alert.processId
            const coordinatedProcessIds = this.coordinatorProcesses || []
            const currentStep = alert.currentStep

            const isAlertCreatedByCurrentUser =
                alert.createdBy === currentUserId

            const isCoordinatorForAlertProcess =
                processId != null && coordinatedProcessIds.includes(processId)

            const isSupervisorOrApprovalStep =
                currentStep === 'supervisor' ||
                currentStep === 'supervisor_approval'

            // Coordinators only care about alerts still in supervisor queue (not pm_designer / resolved step).
            const isCoordinatorSupervisorFlowVisible =
                isCoordinatorForAlertProcess && isSupervisorOrApprovalStep

            let matchesProjectManagerOrDesignerOnWorkOrder = false
            if (alert.workOrder) {
                const workOrder = alert.workOrder
                const projectManagerId =
                    typeof workOrder.projectManager === 'object'
                        ? workOrder.projectManager?.id ??
                          workOrder.projectManager
                        : workOrder.projectManager
                const workOrderCreatedById =
                    typeof workOrder.createdBy === 'object'
                        ? workOrder.createdBy?.id ?? workOrder.createdBy
                        : workOrder.createdBy
                matchesProjectManagerOrDesignerOnWorkOrder =
                    projectManagerId === currentUserId ||
                    workOrderCreatedById === currentUserId
            }

            const isPmDesignerStepVisible =
                currentStep === 'pm_designer' &&
                matchesProjectManagerOrDesignerOnWorkOrder

            const isPmDesignerResolvedAlertVisible =
                alert.status === 'resolved' &&
                matchesProjectManagerOrDesignerOnWorkOrder

            return (
                isAlertCreatedByCurrentUser ||
                isCoordinatorSupervisorFlowVisible ||
                isPmDesignerStepVisible ||
                isPmDesignerResolvedAlertVisible
            )
        },
        /**
         * How this alert counts toward the user's summary: 'resolved', 'open', or null (not shown in open/resolved columns).
         */
        _getAlertSummaryBucket(alert) {
            if (this.canApproveAlerts) {
                const currentStep = alert.currentStep
                const isInSupervisorQueue =
                    currentStep === 'supervisor_approval' ||
                    currentStep === 'supervisor'
                if (!isInSupervisorQueue) {
                    return null
                }
                const isResolved = alert.status === 'resolved'
                return isResolved ? 'resolved' : 'open'
            }
            if (!this._alertIsVisibleToCurrentUser(alert)) {
                return null
            }
            return alert.status === 'resolved' ? 'resolved' : 'open'
        },
        setupRealtimeAlertsListener() {
            try {
                let firstSnapshot = true
                // Setup real-time listener for alerts
                const alertsCollection = firestore
                    .collection('companies')
                    .doc(this.companyId)
                    .collection('alerts')

                this.alertsListener = alertsCollection.onSnapshot(
                    snapshot => {
                        // Create a map of workOrderId -> alert summaries
                        const summariesMap = {}

                        // Process all documents in the alerts collection
                        snapshot.docs.forEach(doc => {
                            const data = doc.data()
                            const workOrderId = doc.id

                            // Initialize summary for this work order
                            if (!summariesMap[workOrderId]) {
                                summariesMap[workOrderId] = {
                                    total: 0,
                                    open: 0,
                                    resolved: 0,
                                    hasAlerts: false,
                                    allResolved: false,
                                }
                            }

                            // Extract all alerts from the alerts object
                            if (
                                data.alerts &&
                                typeof data.alerts === 'object'
                            ) {
                                Object.keys(data.alerts).forEach(alertId => {
                                    const alert = data.alerts[alertId]
                                    summariesMap[workOrderId].total++

                                    const bucket = this._getAlertSummaryBucket(
                                        alert
                                    )
                                    if (bucket === 'resolved') {
                                        summariesMap[workOrderId].resolved++
                                    } else if (bucket === 'open') {
                                        summariesMap[workOrderId].open++
                                    }
                                })
                            }

                            // Set flags
                            if (summariesMap[workOrderId].total > 0) {
                                summariesMap[workOrderId].hasAlerts = true
                                summariesMap[workOrderId].allResolved =
                                    summariesMap[workOrderId].open === 0
                            }
                        })

                        // Update alert summaries for existing work orders
                        this.workOrders.forEach(wo => {
                            const summary = summariesMap[wo.id] || {
                                total: 0,
                                open: 0,
                                resolved: 0,
                                hasAlerts: false,
                                allResolved: false,
                            }
                            this.$set(wo, 'alertSummary', summary)
                        })
                        if (firstSnapshot) {
                            firstSnapshot = false
                        }
                    },
                    error => {
                        console.error('Error in alerts listener:', error)
                        this.setErrorItems({
                            source: this.$options.name,
                            message: error.message,
                        })
                    }
                )
            } catch (error) {
                console.error('Error setting up alerts listener:', error)
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        getRowClass(item) {
            const summary = item.alertSummary || {
                total: 0,
                open: 0,
                resolved: 0,
                pendingApproval: 0,
                hasAlerts: false,
                allResolved: false,
            }
            if (!summary.hasAlerts) return ''

            if (summary.pendingApproval > 0) {
                return 'alert-pending-approval-row'
            }
            if (summary.open > 0) {
                return 'alert-open-row'
            }
            if (summary.allResolved && summary.total > 0) {
                return 'alert-resolved-row'
            }
            return ''
        },
        getAlertIconColor(item) {
            const summary = item.alertSummary || {
                total: 0,
                open: 0,
                resolved: 0,
                hasAlerts: false,
                allResolved: false,
            }
            if (!summary.hasAlerts) return 'grey'
            if (summary.allResolved) return 'blue'
            return 'orange'
        },
        getAlertTooltip(item) {
            const summary = item.alertSummary || {
                total: 0,
                open: 0,
                resolved: 0,
                hasAlerts: false,
                allResolved: false,
            }
            if (!summary.hasAlerts) return ''
            if (summary.allResolved) {
                return `All ${summary.total} alert(s) resolved`
            }
            return `${summary.open} open alert(s), ${summary.resolved} resolved`
        },
    },
}
</script>

<style>
.v-input .v-label {
    font-size: 12px;
}
.alert-open-row {
    background-color: #fff9e6 !important;
}
.alert-resolved-row {
    background-color: #e6f3ff !important;
}
.alert-pending-approval-row {
    background-color: #f3e5f5 !important;
}
</style>
